import React, { useState, useEffect } from 'react';
import { apiCall } from '../utils/api';

export default function TaskForm({ onTaskCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const data = await apiCall('/api/admin/users');
        setAllowedUsers(data.filter((u) => u.enabled));
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Task title is required');
      return;
    }

    setLoading(true);

    try {
      const newTask = await apiCall('/api/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title,
          description,
          assignedTo,
          dueDate,
          status: 'todo',
        }),
      });

      // Send assignment email if assigned to someone
      if (assignedTo.trim()) {
        try {
          await apiCall('/api/tasks/send-assignment-email', {
            method: 'POST',
            body: JSON.stringify({
              taskTitle: title,
              taskDescription: description,
              dueDate,
              assignedTo,
              assignedBy: localStorage.getItem('userEmail') || 'Team',
            }),
          });
        } catch (emailErr) {
          console.error('Error sending assignment email:', emailErr);
          // Don't fail the task creation if email fails
        }
      }

      onTaskCreated(newTask);
      setTitle('');
      setDescription('');
      setAssignedTo('');
      setDueDate('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="task-form">
      <div className="form-group">
        <label htmlFor="title">Task title *</label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Review track specifications"
          disabled={loading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional details about this task"
          rows="3"
          disabled={loading}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="assignedTo">Assign to</label>
          {loadingUsers ? (
            <div style={{ padding: '8px', color: '#999' }}>Loading team members...</div>
          ) : (
            <select
              id="assignedTo"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              disabled={loading || allowedUsers.length === 0}
            >
              <option value="">-- No assignment --</option>
              {allowedUsers.map((user) => (
                <option key={user.email} value={user.email}>
                  {user.email}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="dueDate">Due date</label>
          <input
            id="dueDate"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            disabled={loading}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <button type="submit" disabled={loading} className="btn-primary">
        {loading ? 'Creating...' : 'Create task'}
      </button>
    </form>
  );
}
